/**
 * REDA Authentication Library
 * Universal AJAX-based authentication handler for REDA e-shop projects
 *
 * @version 1.0.0
 * @author REDA a.s.
 * @license MIT
 */

class RedaAuth {
    /**
     * Vytvoření instance RedaAuth
     * @param {Object} config - Konfigurační objekt
     */
    constructor(config = {}) {
        // Výchozí konfigurace
        this.config = {
            // Debug mode
            debug: false,

            // API endpoints
            endpoints: {
                login: '/index.php',
                register: '/index.php',
                forgotPassword: '/index.php',
                oneTimePassword: '/index.php',
                loadDivisions: '/index.php',
                loadApprovers: '/index.php',
                aresApi: 'https://ares.gov.cz/ekonomicke-subjekty-v-be/rest/ekonomicke-subjekty'
            },

            // Routing parameters (io, aio, subio)
            routing: {
                login: {
                    io: 'login',
                    aio: null,
                    subio: null,
                    additionalParams: {}
                },
                register: {
                    io: 'registration',
                    aio: null,
                    subio: null,
                    additionalParams: {}
                },
                forgotPassword: {
                    io: 'fgpwd',
                    aio: null,
                    subio: null,
                    additionalParams: {
                        step: 'send',
                        ic: '0',
                        tName: 'index-login'
                    }
                },
                oneTimePassword: {
                    // Pro generování OTP
                    generate: {
                        io: 'login',
                        aio: null,
                        subio: 'gen',
                        additionalParams: {}
                    },
                    // Pro přihlášení s OTP
                    login: {
                        io: 'login',
                        aio: null,
                        subio: 'login',
                        additionalParams: {}
                    }
                },
                loadDivisions: {
                    io: 'custom',
                    aio: null,
                    subio: 'divisionList',
                    method: 'GET'
                },
                loadApprovers: {
                    io: 'custom',
                    aio: null,
                    subio: 'approversList',
                    method: 'GET'
                }
            },

            // Routing mode: 'mixed' = POST uses formData, GET uses queryString
            routingMode: 'mixed',

            // Features enable/disable
            features: {
                useTabs: false,           // Použít tab interface
                useModals: false,         // Použít Bootstrap modaly
                useOTP: false,            // Jednorázový přístup
                useAres: false,           // ARES integrace
                useDynamicLoading: false, // Dynamické načítání (divize, schvalovatelé)
                autoHideAlerts: true,     // Automaticky skrýt alerty po 5s
                rememberTab: false        // Pamatovat si poslední aktivní tab
            },

            // DOM selektory
            selectors: {
                // Forms
                loginForm: '#loginForm',
                registerForm: '#registerForm',
                osvcRegisterForm: '#osvcRegisterForm',
                forgotPasswordForm: '#forgotPasswordForm',
                oneTimeForm: '#oneTimeForm',

                // Alert container
                alertContainer: '#alertMessage',
                alertText: '#alertText',
                alertIcon: '#alertIcon',

                // Tabs (pokud useTabs = true)
                loginTab: '#loginTab',
                registerTab: '#registerTab',
                oneTimeTab: '#oneTimeTab',
                tabNavLinks: '.login-tabs .nav-link',

                // Login fields
                loginUsername: '#logininput',
                loginPassword: '#pwdinp',
                loginSubmitBtn: null, // Auto-detect submit button

                // Register fields
                registerUsername: '#regUsername',
                registerPassword: '#regPassword',
                registerPasswordConfirm: '#regPasswordConfirm',
                registerEmail: '#regEmail',
                registerFirstname: '#regFirstname',
                registerLastname: '#regLastname',
                registerPhone: '#regPhone',
                registerSubmitBtn: '#regSubmitBtn',

                // OSVČ register fields
                osvcIco: '#osvcIco',
                osvcCompanyName: '#osvcCompanyName',
                osvcStreet: '#osvcStreet',
                osvcHouseNumber: '#osvcHouseNumber',
                osvcCity: '#osvcCity',
                osvcSubmitBtn: '#osvcSubmitBtn',
                aresBtn: '#aresBtn',

                // Forgot password fields
                forgotEmail: '#forgotEmail',
                forgotSubmitBtn: '#forgotBtn',
                forgotPasswordSection: '#forgotPasswordSection',

                // One-time password fields
                otpEmail: '#otpEmail',
                otpPassword: '#otpPassword',
                otpPasswordGroup: '#otpPasswordGroup',
                otpSubaction: '#otpSubaction',
                otpGenBtn: '#otpGenBtn',
                otpLoginBtn: '#otpLoginBtn',

                // Dynamic loading
                countrySelect: '#regCountry',
                divisionSelect: '#regDivision',
                approverSelect: '#regApprover',

                // Register type selection
                registerTypeSelection: '#registerTypeSelection',
                regularRegisterForm: '#regularRegisterForm',
                osvcRegisterFormContainer: '#osvcRegisterForm'
            },

            // Validační pravidla
            validation: {
                password: {
                    minLength: 8,
                    requireMatch: true,
                    pattern: null
                },
                username: {
                    minLength: 4,
                    pattern: null
                },
                email: {
                    required: true
                },
                ico: {
                    length: 8,
                    pattern: '[0-9]*'
                }
            },

            // Texty zpráv (přizpůsobitelné)
            messages: {
                // Success messages
                loginSuccess: 'Přihlášení proběhlo úspěšně.',
                registerSuccess: 'Registrace byla úspěšná! Můžete se přihlásit.',
                forgotPasswordSuccess: 'Instrukce pro obnovení hesla byly odeslány na váš e-mail.',
                otpGenerated: 'Jednorázové heslo bylo odesláno na váš e-mail.',
                aresSuccess: 'Data z ARES byla úspěšně načtena.',

                // Error messages
                loginError: 'Chyba při přihlášení. Zkontrolujte přihlašovací údaje.',
                registerError: 'Chyba při registraci. Zkontrolujte zadané údaje.',
                forgotPasswordError: 'E-mailová adresa nebyla nalezena.',
                otpError: 'E-mailová adresa nebyla nalezena v systému.',
                aresError: 'Chyba při načítání dat z ARES. Zkontrolujte IČO.',
                networkError: 'Došlo k chybě komunikace se serverem.',

                // Validation messages
                passwordsDontMatch: 'Hesla se neshodují.',
                passwordTooShort: 'Heslo musí mít alespoň {minLength} znaků.',
                usernameTooShort: 'Uživatelské jméno musí mít alespoň {minLength} znaků.',
                emailRequired: 'Zadejte prosím e-mailovou adresu.',
                icoInvalid: 'Zadejte platné 8místné IČO.',
                fieldRequired: 'Toto pole je povinné.',

                // Button states
                loading: {
                    login: 'Přihlašuji...',
                    register: 'Registruji...',
                    forgotPassword: 'Odesílám...',
                    otpGenerate: 'Generuji...',
                    ares: 'Načítám...'
                }
            },

            // Callback funkce
            callbacks: {
                onLoginSuccess: null,
                onLoginError: null,
                onRegisterSuccess: null,
                onRegisterError: null,
                onForgotPasswordSuccess: null,
                onForgotPasswordError: null,
                onOTPGenerated: null,
                onOTPError: null,
                onAresSuccess: null,
                onAresError: null,
                onValidationError: null,
                onBeforeSubmit: null,
                onAfterSubmit: null
            },

            // Alert icons (SVG)
            icons: {
                success: '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline>',
                error: '<circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line>',
                info: '<circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line>'
            },

            // Merge s uživatelskou konfigurací
            ...config
        };

        // Deep merge pro nested objekty
        this.config = this._deepMerge(this.config, config);

        // Inicializace
        this.init();
    }

    /**
     * Deep merge objektů
     */
    _deepMerge(target, source) {
        const output = Object.assign({}, target);
        if (this._isObject(target) && this._isObject(source)) {
            Object.keys(source).forEach(key => {
                if (this._isObject(source[key])) {
                    if (!(key in target))
                        Object.assign(output, { [key]: source[key] });
                    else
                        output[key] = this._deepMerge(target[key], source[key]);
                } else {
                    Object.assign(output, { [key]: source[key] });
                }
            });
        }
        return output;
    }

    _isObject(item) {
        return item && typeof item === 'object' && !Array.isArray(item);
    }

    /**
     * Sestaví kompletní URL s routing parametry (pro GET requesty)
     * @param {string} endpointKey - Klíč endpointu (login, register, loadDivisions, ...)
     * @param {string|null} operation - Název operace (generate, login pro oneTimePassword)
     * @param {object} extraParams - Další parametry do query stringu (např. country, division_id)
     * @returns {string} Kompletní URL s parametry
     */
    _buildRequestUrl(endpointKey, operation = null, extraParams = {}) {
        const endpoint = this.config.endpoints[endpointKey];
        const routing = operation
            ? this.config.routing[endpointKey]?.[operation]
            : this.config.routing[endpointKey];

        if (!routing) {
            return endpoint;
        }

        const params = new URLSearchParams();

        // Přidat io, aio, subio
        if (routing.io) params.append('io', routing.io);
        if (routing.aio) params.append('aio', routing.aio);
        if (routing.subio) params.append('subio', routing.subio);

        // Přidat další parametry z routing konfigurace
        if (routing.additionalParams) {
            Object.entries(routing.additionalParams).forEach(([key, value]) => {
                if (value !== null && value !== undefined) {
                    params.append(key, value);
                }
            });
        }

        // Přidat extra parametry (např. country, division_id)
        Object.entries(extraParams).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                params.append(key, value);
            }
        });

        const queryString = params.toString();
        return queryString ? `${endpoint}?${queryString}` : endpoint;
    }

    /**
     * Přidá routing parametry do FormData (pro POST requesty)
     * Zachovává zpětnou kompatibilitu - pokud FormData již obsahuje io/aio/subio z hidden inputů, neměnit je
     * @param {FormData} formData - FormData objekt
     * @param {string} endpointKey - Klíč endpointu (login, register, forgotPassword, ...)
     * @param {string|null} operation - Název operace (generate, login pro oneTimePassword)
     * @returns {FormData} FormData s přidanými routing parametry
     */
    _addRoutingToFormData(formData, endpointKey, operation = null) {
        const routing = operation
            ? this.config.routing[endpointKey]?.[operation]
            : this.config.routing[endpointKey];

        if (!routing) return formData;

        // Přidat io, aio, subio (pokud již nejsou v FormData z hidden inputů)
        if (routing.io && !formData.has('io')) {
            formData.append('io', routing.io);
        }
        if (routing.aio && !formData.has('aio')) {
            formData.append('aio', routing.aio);
        }
        if (routing.subio && !formData.has('subio')) {
            formData.append('subio', routing.subio);
        }

        // Přidat další parametry z routing konfigurace
        if (routing.additionalParams) {
            Object.entries(routing.additionalParams).forEach(([key, value]) => {
                if (value !== null && value !== undefined && !formData.has(key)) {
                    formData.append(key, value);
                }
            });
        }

        return formData;
    }

    /**
     * Debug log
     */
    _log(...args) {
        if (this.config.debug) {
            console.log('[RedaAuth]', ...args);
        }
    }

    /**
     * Inicializace event listeners
     */
    init() {
        this._log('Initializing RedaAuth...', this.config);

        // Login form
        this._attachFormHandler(
            this.config.selectors.loginForm,
            this.handleLogin.bind(this)
        );

        // Register forms
        this._attachFormHandler(
            this.config.selectors.registerForm,
            this.handleRegister.bind(this)
        );

        if (this.config.features.useAres) {
            this._attachFormHandler(
                this.config.selectors.osvcRegisterForm,
                this.handleOsvcRegister.bind(this)
            );
        }

        // Forgot password form
        this._attachFormHandler(
            this.config.selectors.forgotPasswordForm,
            this.handleForgotPassword.bind(this)
        );

        // One-time password form
        if (this.config.features.useOTP) {
            this._attachFormHandler(
                this.config.selectors.oneTimeForm,
                this.handleOneTimeAccess.bind(this)
            );
        }

        // Dynamic loading listeners
        if (this.config.features.useDynamicLoading) {
            this._initDynamicLoading();
        }

        // ARES button
        if (this.config.features.useAres) {
            this._attachClickHandler(
                this.config.selectors.aresBtn,
                this.fetchAresData.bind(this)
            );
        }

        // Tabs (pokud jsou aktivní)
        if (this.config.features.useTabs && this.config.features.rememberTab) {
            this._restoreActiveTab();
        }

        this._log('RedaAuth initialized');
    }

    /**
     * Připojit submit handler na formulář
     */
    _attachFormHandler(selector, handler) {
        const form = document.querySelector(selector);
        if (form) {
            form.addEventListener('submit', handler);
            this._log('Attached form handler to', selector);
        } else {
            this._log('Form not found:', selector);
        }
    }

    /**
     * Připojit click handler
     */
    _attachClickHandler(selector, handler) {
        const element = document.querySelector(selector);
        if (element) {
            element.addEventListener('click', handler);
            this._log('Attached click handler to', selector);
        }
    }

    /**
     * Získat element podle selektoru
     */
    _getElement(selector) {
        if (!selector) return null;
        return document.querySelector(selector);
    }

    /**
     * Zobrazit alert zprávu
     */
    showAlert(type, message) {
        const alertEl = this._getElement(this.config.selectors.alertContainer);
        const alertText = this._getElement(this.config.selectors.alertText);
        const alertIcon = this._getElement(this.config.selectors.alertIcon);

        if (!alertEl) {
            // Fallback na console pokud není alert container
            console[type === 'success' ? 'log' : 'error'](message);

            // Fallback na browser alert
            if (type === 'danger' || type === 'error') {
                alert(message);
            }
            return;
        }

        // Odstranit předchozí třídy
        alertEl.classList.remove('d-none', 'alert-success', 'alert-danger', 'alert-info');
        alertEl.classList.add('alert-' + type);

        // Nastavit text
        if (alertText) {
            alertText.textContent = message;
        } else {
            alertEl.textContent = message;
        }

        // Změnit ikonu
        if (alertIcon) {
            const iconType = type === 'danger' ? 'error' : type;
            alertIcon.innerHTML = this.config.icons[iconType] || this.config.icons.info;
        }

        // Automaticky skrýt
        if (this.config.features.autoHideAlerts) {
            setTimeout(() => {
                alertEl.classList.add('d-none');
            }, 5000);
        }

        this._log('Alert shown:', type, message);
    }

    /**
     * Skrýt alert
     */
    hideAlert() {
        const alertEl = this._getElement(this.config.selectors.alertContainer);
        if (alertEl) {
            alertEl.classList.add('d-none');
        }
    }

    /**
     * Nastavit loading state tlačítka
     */
    setLoading(button, isLoading, loadingText = 'Načítám...') {
        if (!button) return;

        if (isLoading) {
            button.dataset.originalText = button.textContent;
            button.disabled = true;
            button.textContent = loadingText;
        } else {
            button.disabled = false;
            button.textContent = button.dataset.originalText || 'Odeslat';
        }
    }

    /**
     * Validace hesel
     */
    validatePasswords(password, confirmPassword) {
        const rules = this.config.validation.password;

        if (rules.requireMatch && password !== confirmPassword) {
            return {
                valid: false,
                message: this.config.messages.passwordsDontMatch
            };
        }

        if (password.length < rules.minLength) {
            return {
                valid: false,
                message: this.config.messages.passwordTooShort.replace('{minLength}', rules.minLength)
            };
        }

        if (rules.pattern && !new RegExp(rules.pattern).test(password)) {
            return {
                valid: false,
                message: 'Heslo nesplňuje požadovaný formát.'
            };
        }

        return { valid: true };
    }

    /**
     * Validace uživatelského jména
     */
    validateUsername(username) {
        const rules = this.config.validation.username;

        if (username.length < rules.minLength) {
            return {
                valid: false,
                message: this.config.messages.usernameTooShort.replace('{minLength}', rules.minLength)
            };
        }

        if (rules.pattern && !new RegExp(rules.pattern).test(username)) {
            return {
                valid: false,
                message: 'Uživatelské jméno obsahuje nepovolené znaky.'
            };
        }

        return { valid: true };
    }

    /**
     * Handler: Přihlášení
     */
    async handleLogin(event) {
        event.preventDefault();
        this._log('handleLogin called');

        const form = event.target;
        const submitBtn = form.querySelector('button[type="submit"]') ||
                         this._getElement(this.config.selectors.loginSubmitBtn);

        // Validate form first
        if (!form.checkValidity()) {
            event.stopPropagation();
            form.classList.add('was-validated');
            this._log('Form validation failed');
            return;
        }

        // Callback před odesláním
        if (this.config.callbacks.onBeforeSubmit) {
            const proceed = await this.config.callbacks.onBeforeSubmit('login', form);
            if (proceed === false) return;
        }

        this.setLoading(submitBtn, true, this.config.messages.loading.login);

        try {
            // Check if using aio endpoint (expects JSON)
            const routing = this.config.routing.login;
            const useJson = routing && routing.aio;

            let body, headers;

            if (useJson) {
                // For aio endpoints: send JSON
                const formData = new FormData(form);
                const jsonData = {};

                // Convert FormData to JSON object
                for (let [key, value] of formData.entries()) {
                    jsonData[key] = value;
                }

                // Add routing parameters
                if (routing.aio) jsonData.aio = routing.aio;
                if (routing.io) jsonData.io = routing.io;
                if (routing.subio) jsonData.subio = routing.subio;

                // Add additional params
                if (routing.additionalParams) {
                    Object.assign(jsonData, routing.additionalParams);
                }

                body = JSON.stringify(jsonData);
                headers = {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                };
            } else {
                // For io endpoints: send FormData
                let formData = new FormData(form);
                formData = this._addRoutingToFormData(formData, 'login');

                body = formData;
                headers = {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/json'
                };
            }

            const response = await fetch(this.config.endpoints.login, {
                method: 'POST',
                headers: headers,
                body: body,
                credentials: 'include'  // Posílat cookies
            });

            // Try to parse as JSON
            let data;
            try {
                data = await response.json();
                this._log('Login response:', data);
            } catch (jsonError) {
                // Backend nevrátil validní JSON
                this._log('Response is not valid JSON, checking HTTP status:', response.status);

                // Pokud je HTTP status OK (200-299), považujme to za úspěch
                // protože backend pravděpodobně vytvořil session
                if (response.ok) {
                    this._log('HTTP status is OK, treating as successful login');
                    data = { success: true, result: 1 };
                } else {
                    throw new Error('Backend returned non-JSON response with error status ' + response.status);
                }
            }

            // Check for success: prioritize result field, then success field, then HTTP status
            const isSuccess = data.result === 1 || data.result === '1' ||
                            (data.success === true && data.result !== 0);

            if (isSuccess) {
                this.showAlert('success', data.message || this.config.messages.loginSuccess);

                // Callback on success
                if (this.config.callbacks.onLoginSuccess) {
                    await this.config.callbacks.onLoginSuccess(data);
                } else {
                    // Default behavior - reload nebo redirect
                    setTimeout(() => {
                        window.location.reload();
                    }, 1000);
                }
            } else {
                // Login failed
                const errorMsg = data.error_message || data.message || this.config.messages.loginError;
                this.showAlert('danger', errorMsg);
                this._log('Login failed:', data);

                if (this.config.callbacks.onLoginError) {
                    this.config.callbacks.onLoginError(data);
                }
            }
        } catch (error) {
            console.error('Login error:', error);
            this.showAlert('danger', this.config.messages.networkError);

            if (this.config.callbacks.onLoginError) {
                this.config.callbacks.onLoginError(error);
            }
        } finally {
            this.setLoading(submitBtn, false);

            if (this.config.callbacks.onAfterSubmit) {
                this.config.callbacks.onAfterSubmit('login', form);
            }
        }
    }

    /**
     * Handler: Registrace (běžná)
     */
    async handleRegister(event) {
        event.preventDefault();
        this._log('handleRegister called');

        const form = event.target;
        const submitBtn = this._getElement(this.config.selectors.registerSubmitBtn) ||
                         form.querySelector('button[type="submit"]');

        // Validace hesel
        const password = this._getElement(this.config.selectors.registerPassword)?.value;
        const confirmPassword = this._getElement(this.config.selectors.registerPasswordConfirm)?.value;

        if (password && confirmPassword) {
            const validation = this.validatePasswords(password, confirmPassword);
            if (!validation.valid) {
                this.showAlert('danger', validation.message);

                if (this.config.callbacks.onValidationError) {
                    this.config.callbacks.onValidationError('password', validation.message);
                }
                return;
            }
        }

        // Callback před odesláním
        if (this.config.callbacks.onBeforeSubmit) {
            const proceed = await this.config.callbacks.onBeforeSubmit('register', form);
            if (proceed === false) return;
        }

        this.setLoading(submitBtn, true, this.config.messages.loading.register);

        try {
            let formData = new FormData(form);

            // Přidat routing parametry (mixed režim - do FormData pro POST)
            formData = this._addRoutingToFormData(formData, 'register');

            const response = await fetch(this.config.endpoints.register, {
                method: 'POST',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/json'
                },
                body: formData,
                credentials: 'include'
            });

            // Try to parse as JSON, but fallback to HTTP status if that fails
            let data;
            try {
                data = await response.json();
            } catch (jsonError) {
                this._log('Response is not valid JSON, checking HTTP status:', response.status);

                if (response.ok) {
                    this._log('HTTP status is OK, treating as successful registration');
                    data = { success: true, result: 1 };
                } else {
                    throw new Error('Backend returned non-JSON response with error status ' + response.status);
                }
            }

            if (data.success || data.result == 1) {
                this.showAlert('success', data.message || this.config.messages.registerSuccess);
                form.reset();

                // Callback on success
                if (this.config.callbacks.onRegisterSuccess) {
                    await this.config.callbacks.onRegisterSuccess(data);
                } else {
                    // Default: přepnout na login tab po 2s
                    setTimeout(() => {
                        if (this.config.features.useTabs) {
                            this.switchTab('login');
                        }
                    }, 2000);
                }
            } else {
                this.showAlert('danger', data.message || this.config.messages.registerError);

                if (this.config.callbacks.onRegisterError) {
                    this.config.callbacks.onRegisterError(data);
                }
            }
        } catch (error) {
            console.error('Register error:', error);
            this.showAlert('danger', this.config.messages.networkError);

            if (this.config.callbacks.onRegisterError) {
                this.config.callbacks.onRegisterError(error);
            }
        } finally {
            this.setLoading(submitBtn, false);

            if (this.config.callbacks.onAfterSubmit) {
                this.config.callbacks.onAfterSubmit('register', form);
            }
        }
    }

    /**
     * Handler: OSVČ registrace
     */
    async handleOsvcRegister(event) {
        event.preventDefault();
        this._log('handleOsvcRegister called');

        const form = event.target;
        const submitBtn = this._getElement(this.config.selectors.osvcSubmitBtn) ||
                         form.querySelector('button[type="submit"]');

        // Stejná logika jako handleRegister
        await this.handleRegister.call(this, event);
    }

    /**
     * Handler: Zapomenuté heslo
     */
    async handleForgotPassword(event) {
        event.preventDefault();
        this._log('handleForgotPassword called');

        const form = event.target;
        const submitBtn = this._getElement(this.config.selectors.forgotSubmitBtn) ||
                         form.querySelector('button[type="submit"]');
        const emailInput = this._getElement(this.config.selectors.forgotEmail);

        // Validate form first (HTML5 validation)
        if (!form.checkValidity()) {
            event.stopPropagation();
            form.classList.add('was-validated');
            this._log('Form validation failed');
            return;
        }

        // Validace emailu
        if (emailInput && !emailInput.value) {
            this.showAlert('danger', this.config.messages.emailRequired);
            return;
        }

        // Callback před odesláním
        if (this.config.callbacks.onBeforeSubmit) {
            const proceed = await this.config.callbacks.onBeforeSubmit('forgotPassword', form);
            if (proceed === false) return;
        }

        this.setLoading(submitBtn, true, this.config.messages.loading.forgotPassword);

        try {
            // Check if using aio endpoint (expects JSON)
            const routing = this.config.routing.forgotPassword;
            const useJson = routing && routing.aio;

            let body, headers;

            if (useJson) {
                // For aio endpoints: send JSON
                const formData = new FormData(form);
                const jsonData = {};

                // Convert FormData to JSON object
                for (let [key, value] of formData.entries()) {
                    jsonData[key] = value;
                }

                // Add routing parameters
                if (routing.aio) jsonData.aio = routing.aio;
                if (routing.io) jsonData.io = routing.io;
                if (routing.subio) jsonData.subio = routing.subio;

                // Add additional params
                if (routing.additionalParams) {
                    Object.assign(jsonData, routing.additionalParams);
                }

                body = JSON.stringify(jsonData);
                headers = {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                };
            } else {
                // For io endpoints: send FormData
                let formData = new FormData(form);
                formData = this._addRoutingToFormData(formData, 'forgotPassword');

                body = formData;
                headers = {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/json'
                };
            }

            const response = await fetch(this.config.endpoints.forgotPassword, {
                method: 'POST',
                headers: headers,
                body: body,
                credentials: 'include'
            });

            // Try to parse as JSON, but fallback to HTTP status if that fails
            let data;
            try {
                data = await response.json();
            } catch (jsonError) {
                this._log('Response is not valid JSON, checking HTTP status:', response.status);

                if (response.ok) {
                    this._log('HTTP status is OK, treating as successful password reset');
                    data = { success: true, result: 1 };
                } else {
                    throw new Error('Backend returned non-JSON response with error status ' + response.status);
                }
            }

            if (data.success || data.result == 1) {
                this.showAlert('success', data.message || this.config.messages.forgotPasswordSuccess);
                form.reset();

                // Skrýt sekci zapomenutého hesla
                const forgotSection = this._getElement(this.config.selectors.forgotPasswordSection);
                if (forgotSection) {
                    forgotSection.classList.add('d-none');
                }

                if (this.config.callbacks.onForgotPasswordSuccess) {
                    await this.config.callbacks.onForgotPasswordSuccess(data);
                }
            } else {
                this.showAlert('danger', data.message || this.config.messages.forgotPasswordError);

                if (this.config.callbacks.onForgotPasswordError) {
                    this.config.callbacks.onForgotPasswordError(data);
                }
            }
        } catch (error) {
            console.error('Forgot password error:', error);
            this.showAlert('danger', this.config.messages.networkError);

            if (this.config.callbacks.onForgotPasswordError) {
                this.config.callbacks.onForgotPasswordError(error);
            }
        } finally {
            this.setLoading(submitBtn, false);

            if (this.config.callbacks.onAfterSubmit) {
                this.config.callbacks.onAfterSubmit('forgotPassword', form);
            }
        }
    }

    /**
     * Handler: Jednorázový přístup (OTP)
     */
    async handleOneTimeAccess(event) {
        if (!this.config.features.useOTP) return;

        const submitBtn = event.submitter;
        const subaction = submitBtn?.getAttribute('data-subaction');
        const form = event.target;
        const emailInput = this._getElement(this.config.selectors.otpEmail);
        const passwordInput = this._getElement(this.config.selectors.otpPassword);
        const passwordGroup = this._getElement(this.config.selectors.otpPasswordGroup);
        const genBtn = this._getElement(this.config.selectors.otpGenBtn);
        const loginBtn = this._getElement(this.config.selectors.otpLoginBtn);
        const subactionInput = this._getElement(this.config.selectors.otpSubaction);

        // Nastavit subaction
        if (subactionInput) {
            subactionInput.value = subaction;
        }

        if (subaction === 'gen') {
            event.preventDefault();
            this._log('handleOneTimeAccess: generate');

            // Validace emailu
            if (!emailInput?.value) {
                this.showAlert('danger', this.config.messages.emailRequired);
                return;
            }

            this.setLoading(submitBtn, true, this.config.messages.loading.otpGenerate);

            try {
                let formData = new FormData(form);

                // Přidat routing parametry pro generování OTP
                formData = this._addRoutingToFormData(formData, 'oneTimePassword', 'generate');

                const response = await fetch(this.config.endpoints.oneTimePassword, {
                    method: 'POST',
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                        'Accept': 'application/json'
                    },
                    body: formData,
                    credentials: 'include'
                });

                const data = await response.json();

                if (data.result == 1 || data.success) {
                    this.showAlert('success', data.message || this.config.messages.otpGenerated);

                    // Zobrazit pole pro heslo
                    if (passwordGroup) passwordGroup.classList.remove('d-none');
                    if (loginBtn) loginBtn.classList.remove('d-none');
                    if (genBtn) genBtn.classList.add('d-none');

                    // Zamknout email
                    if (emailInput) {
                        emailInput.setAttribute('readonly', 'readonly');
                        emailInput.classList.add('bg-light');
                    }

                    // Zaměřit heslo
                    if (passwordInput) passwordInput.focus();

                    if (this.config.callbacks.onOTPGenerated) {
                        this.config.callbacks.onOTPGenerated(data);
                    }
                } else {
                    this.showAlert('danger', data.message || this.config.messages.otpError);

                    if (this.config.callbacks.onOTPError) {
                        this.config.callbacks.onOTPError(data);
                    }
                }
            } catch (error) {
                console.error('OTP generate error:', error);
                this.showAlert('danger', this.config.messages.networkError);
            } finally {
                this.setLoading(submitBtn, false);
            }
        } else if (subaction === 'login') {
            // Přihlášení s OTP - validace
            if (!emailInput?.value || !passwordInput?.value) {
                event.preventDefault();
                this.showAlert('danger', 'Zadejte prosím e-mail a heslo.');
                return;
            }

            // Přidat routing parametry pro login s OTP (do FormData před odesláním)
            // Poznámka: V tomto případě se formulář odešle normálně (POST s reload),
            // ale routing parametry by měly být v hidden inputech nebo je přidáme programově
            event.preventDefault();
            let formData = new FormData(form);
            formData = this._addRoutingToFormData(formData, 'oneTimePassword', 'login');

            // Odeslat formulář jako AJAX pro konzistenci
            this.setLoading(submitBtn, true, this.config.messages.loading.login);

            try {
                const response = await fetch(this.config.endpoints.oneTimePassword, {
                    method: 'POST',
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                        'Accept': 'application/json'
                    },
                    body: formData,
                    credentials: 'include'
                });

                const data = await response.json();

                if (data.success || data.result == 1) {
                    this.showAlert('success', data.message || this.config.messages.loginSuccess);

                    if (this.config.callbacks.onLoginSuccess) {
                        await this.config.callbacks.onLoginSuccess(data);
                    } else {
                        setTimeout(() => {
                            window.location.reload();
                        }, 1000);
                    }
                } else {
                    this.showAlert('danger', data.message || this.config.messages.loginError);
                }
            } catch (error) {
                console.error('OTP login error:', error);
                this.showAlert('danger', this.config.messages.networkError);
            } finally {
                this.setLoading(submitBtn, false);
            }
        }
    }

    /**
     * Načíst data z ARES
     */
    async fetchAresData(event) {
        if (!this.config.features.useAres) return;

        if (event) event.preventDefault();
        this._log('fetchAresData called');

        const btn = this._getElement(this.config.selectors.aresBtn);
        const icoInput = this._getElement(this.config.selectors.osvcIco);
        const ico = icoInput?.value;

        // Validace IČO
        if (!ico || ico.length !== this.config.validation.ico.length) {
            this.showAlert('danger', this.config.messages.icoInvalid);
            return;
        }

        this.setLoading(btn, true, this.config.messages.loading.ares);

        try {
            const response = await fetch(`${this.config.endpoints.aresApi}/${ico}`);

            if (!response.ok) {
                throw new Error('IČO nenalezeno v ARES');
            }

            const data = await response.json();

            // Vyplnit název firmy
            if (data.obchodniJmeno) {
                const companyNameInput = this._getElement(this.config.selectors.osvcCompanyName);
                if (companyNameInput) companyNameInput.value = data.obchodniJmeno;
            }

            // Vyplnit adresu
            if (data.sidlo) {
                const address = data.sidlo;

                if (address.nazevUlice) {
                    const streetInput = this._getElement(this.config.selectors.osvcStreet);
                    if (streetInput) streetInput.value = address.nazevUlice;
                }

                if (address.cisloDomovni) {
                    const houseNumberInput = this._getElement(this.config.selectors.osvcHouseNumber);
                    if (houseNumberInput) houseNumberInput.value = address.cisloDomovni;
                }

                if (address.nazevObce) {
                    const cityInput = this._getElement(this.config.selectors.osvcCity);
                    if (cityInput) cityInput.value = address.nazevObce;
                }
            }

            this.showAlert('success', data.message || this.config.messages.aresSuccess);

            if (this.config.callbacks.onAresSuccess) {
                this.config.callbacks.onAresSuccess(data);
            }
        } catch (error) {
            console.error('ARES error:', error);
            this.showAlert('danger', this.config.messages.aresError);

            if (this.config.callbacks.onAresError) {
                this.config.callbacks.onAresError(error);
            }
        } finally {
            this.setLoading(btn, false);
        }
    }

    /**
     * Načíst divize podle státu
     */
    async loadDivisions(country) {
        if (!this.config.features.useDynamicLoading) return;

        this._log('loadDivisions called:', country);

        const divisionSelect = this._getElement(this.config.selectors.divisionSelect);
        const approverSelect = this._getElement(this.config.selectors.approverSelect);

        if (!divisionSelect) return;

        divisionSelect.innerHTML = '<option value="">Vyberte divizi</option>';
        if (approverSelect) {
            approverSelect.innerHTML = '<option value="">Vyberte schvalovatele</option>';
        }

        if (!country) return;

        try {
            // Sestavit URL s routing parametry (GET request)
            const url = this._buildRequestUrl('loadDivisions', null, { country });

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/json'
                },
                credentials: 'include'
            });

            const responseText = await response.text();
            this._log('Load divisions response:', responseText);

            let data;
            try {
                data = JSON.parse(responseText);
            } catch (parseError) {
                console.error('Load divisions - invalid JSON response:', responseText.substring(0, 500));
                this.showAlert('danger', 'Backend nevrátil validní JSON odpověď.');
                return;
            }

            if (data.result == 1 && data.divisions && data.divisions.length > 0) {
                data.divisions.forEach(div => {
                    const option = document.createElement('option');
                    option.value = div.key;
                    option.textContent = div.value;
                    divisionSelect.appendChild(option);
                });
            } else {
                this.showAlert('danger', 'Chyba při načítání divizí.');
            }
        } catch (error) {
            console.error('Load divisions error:', error);
            this.showAlert('danger', 'Chyba při načítání divizí.');
        }
    }

    /**
     * Načíst schvalovatele podle divize
     */
    async loadApprovers(division) {
        if (!this.config.features.useDynamicLoading) return;

        this._log('loadApprovers called:', division);

        const approverSelect = this._getElement(this.config.selectors.approverSelect);

        if (!approverSelect) return;

        approverSelect.innerHTML = '<option value="">Vyberte schvalovatele</option>';

        if (!division) return;

        try {
            // Sestavit URL s routing parametry (GET request)
            const url = this._buildRequestUrl('loadApprovers', null, { division_id: division });

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/json'
                },
                credentials: 'include'
            });

            const responseText = await response.text();
            this._log('Load approvers response:', responseText);

            let data;
            try {
                data = JSON.parse(responseText);
            } catch (parseError) {
                console.error('Load approvers - invalid JSON response:', responseText.substring(0, 500));
                this.showAlert('danger', 'Backend nevrátil validní JSON odpověď.');
                return;
            }

            if (data.result == 1 && data.approvers && data.approvers.length > 0) {
                data.approvers.forEach(app => {
                    const option = document.createElement('option');
                    option.value = app.key;
                    option.textContent = app.value;
                    approverSelect.appendChild(option);
                });
            } else {
                this.showAlert('danger', 'Chyba při načítání schvalovatelů.');
            }
        } catch (error) {
            console.error('Load approvers error:', error);
            this.showAlert('danger', 'Chyba při načítání schvalovatelů.');
        }
    }

    /**
     * Inicializovat dynamic loading listeners
     */
    _initDynamicLoading() {
        const countrySelect = this._getElement(this.config.selectors.countrySelect);
        const divisionSelect = this._getElement(this.config.selectors.divisionSelect);

        if (countrySelect) {
            countrySelect.addEventListener('change', (e) => {
                this.loadDivisions(e.target.value);
            });

            // Pokud je při načtení již vybrán stát, načti divize
            if (countrySelect.value) {
                this.loadDivisions(countrySelect.value);
            }
        }

        if (divisionSelect) {
            divisionSelect.addEventListener('change', (e) => {
                this.loadApprovers(e.target.value);
            });

            // Pokud je při načtení již vybrána divize, načti schvalovatele
            if (divisionSelect.value) {
                this.loadApprovers(divisionSelect.value);
            }
        }
    }

    /**
     * Přepnout tab
     */
    switchTab(tabName, element) {
        if (!this.config.features.useTabs) return;

        this._log('switchTab called:', tabName);

        // Skrýt všechny taby
        const tabs = ['login', 'oneTime', 'register'];
        tabs.forEach(tab => {
            const tabEl = this._getElement(this.config.selectors[tab + 'Tab']);
            if (tabEl) {
                tabEl.classList.add('d-none');
            }
        });

        // Odebrat active z nav links
        const navLinks = document.querySelectorAll(this.config.selectors.tabNavLinks);
        navLinks.forEach(link => {
            link.classList.remove('active');
        });

        // Zobrazit aktivní tab
        const activeTab = this._getElement(this.config.selectors[tabName + 'Tab']);
        if (activeTab) {
            activeTab.classList.remove('d-none');
        }

        // Přidat active na nav link
        if (element) {
            element.classList.add('active');
        }

        // Uložit do localStorage
        if (this.config.features.rememberTab) {
            localStorage.setItem('redaAuthActiveTab', tabName);
        }
    }

    /**
     * Obnovit poslední aktivní tab
     */
    _restoreActiveTab() {
        const activeTab = localStorage.getItem('redaAuthActiveTab');
        if (activeTab) {
            this.switchTab(activeTab);
        }
    }

    /**
     * Toggle zobrazení sekce
     */
    toggleSection(selector) {
        const element = this._getElement(selector);
        if (element) {
            element.classList.toggle('d-none');
        }
    }

    /**
     * Reset formuláře
     */
    resetForm(formSelector) {
        const form = this._getElement(formSelector);
        if (form) {
            form.reset();
        }
    }

    /**
     * Destroy instance (cleanup)
     */
    destroy() {
        this._log('Destroying RedaAuth instance...');
        // Zde by se odstraňovaly event listeners pokud by bylo potřeba
    }
}

// Export pro ES6 modules
export default RedaAuth;

// Export pro window (fallback)
if (typeof window !== 'undefined') {
    window.RedaAuth = RedaAuth;
}